import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { Divider, ScrollView, Typography } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { IRevaultInformation } from 'features/payment/types';
import { isApplePay, isGooglePay, shouldReVault } from 'features/payment/utils';
import noop from 'lodash-es/noop';
import { StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import AccountLayout from 'components/layout/drawer-page';
import LoadingAnimation from 'components/loading-animation';
import PaymentMethodOption from 'components/payment-method-option';
import UserVerificationModal from 'components/user-verification-modal';
import { emptyCartContext, useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { fontSizes } from 'styles/constants/fonts';
import { routes } from 'utils/routing';

import { AddGiftCardLink } from './add-gift-card-link';
import { getPaymentMethodOptionProps } from './get-payment-method-options-props';

interface IPaymentMethodFlatListWithButton {
  setIsAddGiftCardVisible?: (param: boolean) => void;
  setIsTransferGiftCardVisible?: (param: boolean) => void;
}

export const PaymentMethodFlatListWithButton: React.FC<React.PropsWithChildren<
  IPaymentMethodFlatListWithButton
>> = ({ setIsAddGiftCardVisible = noop, setIsTransferGiftCardVisible = noop }) => {
  const {
    deletePaymentMethod,
    loading,
    paymentMethods,
    setDefaultPaymentMethodId,
    defaultPaymentMethodId,
    isOrbital: isOrbitalPaymentEnabled,
  } = usePaymentContext();
  const router = useRouter();
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [isLoading, setLoading] = useState(loading);
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const hasPaymentMethods = paymentMethods.length > 0;
  const [selectedMethod, setSelectedMethod] = useState(defaultPaymentMethodId);

  const mappedPaymentMethods = useMemo(() => {
    return paymentMethods.map(method => {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';

      const isNativePayment = isApplePay(accountIdentifier) || isGooglePay(accountIdentifier);

      const isCashPayment = !!method.cash;
      const isPayPalPayment = !!method.paypal;
      const isVenmoPayment = !!method.venmo;
      const isCashAppPayment = !!method.cashapp;

      const paymentMethodOptionProps = getPaymentMethodOptionProps({
        defaultPaymentMethodId: selectedMethod,
        setDefaultPaymentMethodId: setSelectedMethod,
        accountIdentifier,
      });

      const revaultInformation: IRevaultInformation = shouldReVault({
        accountIdentifier: method.accountIdentifier || '',
        isOrbitalPaymentEnabled,
        methods: paymentMethods,
      });

      return (
        <Fragment key={accountIdentifier}>
          <PaymentMethodOption
            method={method}
            showDefault
            removable={
              !method.prepaid &&
              !isNativePayment &&
              !isCashPayment &&
              !isPayPalPayment &&
              !isVenmoPayment &&
              !isCashAppPayment
            }
            readonly={isNativePayment}
            deletePaymentMethod={deletePaymentMethod}
            isDefaultPaymentMethod={
              accountIdentifier === (selectedMethod || defaultPaymentMethodId)
            }
            fullWidthDescription={false}
            {...paymentMethodOptionProps}
            revaultInformation={revaultInformation}
            isFlatList
            {...(isDigitalGiftCardNumberExposedEnabled && { isMasked: !!method.prepaid })}
          />
          <Divider />
        </Fragment>
      );
    });
  }, [
    paymentMethods,
    selectedMethod,
    isOrbitalPaymentEnabled,
    deletePaymentMethod,
    defaultPaymentMethodId,
    isDigitalGiftCardNumberExposedEnabled,
  ]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (selectedMethod) {
      setDefaultPaymentMethodId(selectedMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod]);

  const isInCart = useCartContext() !== emptyCartContext;
  return (
    <AccountLayout
      headerText={t('paymentMethods')}
      footer={
        <>
          <ActionButton
            mb={'$4'}
            fullWidth
            variant={ActionButtonVariants.OUTLINE}
            onPress={() => {
              router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
            }}
            testID="add-new-payment-method"
          >
            {t('addPaymentMethodOrReload')}
          </ActionButton>
          {enableGiftCard && <AddGiftCardLink />}
        </>
      }
    >
      {isLoading ? (
        <LoadingAnimation />
      ) : (
        <>
          {!hasPaymentMethods ? (
            <Typography textAlign="center">{t('noPaymentMethodOnFile')}</Typography>
          ) : (
            <>
              <View style={styles.defaultTextWrapper}>
                <Typography variant="copyOne" style={styles.defaultText}>
                  {t('accountDefault')}
                </Typography>
              </View>
              <ScrollView width="full" testID="account-saved-payments">
                {mappedPaymentMethods}
              </ScrollView>
            </>
          )}

          {showVerificationModal && (
            <UserVerificationModal showDialog onDismiss={() => setShowVerificationModal(false)} />
          )}
        </>
      )}
    </AccountLayout>
  );
};

const styles = StyleSheet.create({
  defaultTextWrapper: {
    width: '100%',
    position: 'relative',
    height: 40,
    justifyContent: 'center',
  },
  defaultText: {
    textTransform: 'uppercase',
    fontSize: fontSizes.sm,
    marginRight: 10,
    textAlign: 'right',
  },
});
